import { useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import {
    AXNUIContent,
    AXNUIContentHeader,
    AXNUITheme,
    AXNUIThemeHeader
} from "@iddataweb/idw-common-ui/types";
import { ThemingHeaderValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { ColorPickerFormGroup } from "../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingHeaderTextSection } from "./theming-header-text-section/theming-header-text-section";
import { ThemingHeaderLogoSection } from "./theming-header-logo-section/theming-header-logo-section";
import { ThemingHeaderCustomSection } from "./theming-header-custom-section/theming-header-custom-section";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingHeaderTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ThemingHeaderValidationErrors;
    axnuiContent: AXNUIContent,
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUIThemePropertyValue: (axnuiTheme: AXNUITheme) => void;
};

export const ThemingHeaderTabContainer = (props: ThemingHeaderTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        generatePreviewComponent,
        uploadFileToS3,
        updateAXNUIContentPropertyValue,
        updateAXNUIThemePropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIThemeHeaderFieldValue = (key: keyof AXNUIThemeHeader, value: string | boolean) => {
        const newPropertyValue: AXNUITheme = {
            ...axnuiTheme,
            header: {
                ...axnuiTheme.header,
                [key]: value
            }
        };
        updateAXNUIThemePropertyValue(newPropertyValue);
    };

    const updateAXNUIContentHeaderFieldValue = (key: keyof AXNUIContentHeader, value: string) => {
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            header: {
                ...axnuiContent.header,
                [key]: value
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const handleBackgroundColorChange = (newValue: string) => {
        updateAXNUIThemeHeaderFieldValue("backgroundColor", newValue);
    };

    const handleResetBackgroundColor = () => {
        updateAXNUIThemeHeaderFieldValue("backgroundColor", inheritedAXNUITheme.header.backgroundColor);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.HEADER);
    }, [generatePreviewComponent]);

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        {!axnuiTheme.header.useHtmlCss && (
                            <>
                                <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.background.title}>
                                    <ThemingFormGroupWrapper
                                        inheritedThemingPropertyValue={inheritedAXNUITheme.header.backgroundColor}
                                        currentThemingPropertyValue={axnuiTheme.header.backgroundColor}
                                        pageLevel={pageLevel}
                                    >
                                        <ColorPickerFormGroup
                                            controlId={"theming-header-background-color-picker"}
                                            automationId={"theming-header-background-color-picker"}
                                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.background.inputs.backgroundColor.label}
                                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.background.inputs.backgroundColor.label}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            layout={layout}
                                            labelWidth={labelWidth}
                                            value={axnuiTheme.header.backgroundColor || ""}
                                            onChange={handleBackgroundColorChange}
                                            position={"bottom"}
                                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.background.inputs.backgroundColor.tooltip}
                                            afterLabelIcon={isDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetBackgroundColor}
                                        />
                                    </ThemingFormGroupWrapper>
                                </ThemingFormSection>
                                <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerText.title}>
                                    <ThemingHeaderTextSection
                                        axnuiContent={axnuiContent}
                                        axnuiTheme={axnuiTheme}
                                        inheritedAXNUIContent={inheritedAXNUIContent}
                                        inheritedAXNUITheme={inheritedAXNUITheme}
                                        pageLevel={pageLevel}
                                        isDisabled={isDisabled}
                                        labelWidth={labelWidth}
                                        layout={layout}
                                        validationErrors={validationErrors}
                                        updateAXNUIContentHeaderFieldValue={updateAXNUIContentHeaderFieldValue}
                                        updateAXNUIThemeHeaderFieldValue={updateAXNUIThemeHeaderFieldValue}
                                    />
                                </ThemingFormSection>
                                <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.title}>
                                    <ThemingHeaderLogoSection
                                        axnuiContent={axnuiContent}
                                        axnuiTheme={axnuiTheme}
                                        inheritedAXNUIContent={inheritedAXNUIContent}
                                        inheritedAXNUITheme={inheritedAXNUITheme}
                                        pageLevel={pageLevel}
                                        isDisabled={isDisabled}
                                        labelWidth={labelWidth}
                                        layout={layout}
                                        validationErrors={validationErrors}
                                        uploadFileToS3={uploadFileToS3}
                                        updateAXNUIContentHeaderFieldValue={updateAXNUIContentHeaderFieldValue}
                                        updateAXNUIThemeHeaderFieldValue={updateAXNUIThemeHeaderFieldValue}
                                    />
                                </ThemingFormSection>
                            </>
                        )}
                        <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.customHtmlAndCss.title}>
                            <ThemingHeaderCustomSection
                                axnuiContent={axnuiContent}
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUIContent={inheritedAXNUIContent}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateAXNUIContentHeaderFieldValue={updateAXNUIContentHeaderFieldValue}
                                updateAXNUIThemeHeaderFieldValue={updateAXNUIThemeHeaderFieldValue}
                            />
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};